import { useCallback, useEffect, useRef } from 'react';
import prefetchService from 'services/prefetchService';

/**
 * Hook para prefetch inteligente
 * Aquece cache quando usuário troca de empresa ou página
 */
export const usePrefetch = () => {
  const prefetchIdRef = useRef(0);

  /**
   * Prefetch para mudança de empresa
   */
  const prefetchForCompany = useCallback(async (company, context = 'financeiro') => {
    const thisPrefetch = ++prefetchIdRef.current;
    
    try {
      await prefetchService.prefetchForCompany(company, context);
      
      // Verificar se ainda é o prefetch atual
      if (thisPrefetch !== prefetchIdRef.current) {
        return null;
      }
      
      return true;
    } catch (error) {
      console.error('❌ Erro no prefetch da empresa:', error);
      return false;
    }
  }, []);

  /**
   * Prefetch para mudança de contexto
   */
  const prefetchForContext = useCallback(async (context, companies = []) => {
    const thisPrefetch = ++prefetchIdRef.current;
    
    try {
      await prefetchService.prefetchForContext(context, companies);
      
      // Verificar se ainda é o prefetch atual
      if (thisPrefetch !== prefetchIdRef.current) {
        return null;
      }
      
      return true;
    } catch (error) {
      console.error('❌ Erro no prefetch do contexto:', error);
      return false;
    }
  }, []);

  /**
   * Prefetch para navegação
   */
  const prefetchForNavigation = useCallback(async (url) => {
    const thisPrefetch = ++prefetchIdRef.current;
    
    try {
      await prefetchService.prefetchForNavigation(url);
      
      // Verificar se ainda é o prefetch atual
      if (thisPrefetch !== prefetchIdRef.current) {
        return null;
      }
      
      return true;
    } catch (error) {
      console.error('❌ Erro no prefetch da navegação:', error);
      return false;
    }
  }, []);

  /**
   * Obter dados do cache de prefetch
   */
  const getPrefetchedData = useCallback((key) => {
    return prefetchService.getPrefetchedData(key);
  }, []);

  /**
   * Limpar cache de prefetch
   */
  const clearPrefetchCache = useCallback(() => {
    prefetchService.clearPrefetchCache();
  }, []);

  /**
   * Obter estatísticas do prefetch
   */
  const getPrefetchStats = useCallback(() => {
    return prefetchService.getStats();
  }, []);

  /**
   * Prefetch inteligente baseado em hover
   */
  const setupHoverPrefetch = useCallback((element, context) => {
    if (!element) return;

    const handleMouseEnter = () => {
      prefetchForContext(context);
    };

    const handleFocus = () => {
      prefetchForContext(context);
    };

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('focus', handleFocus);

    return () => {
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('focus', handleFocus);
    };
  }, [prefetchForContext]);

  /**
   * Prefetch inteligente para links
   */
  const setupLinkPrefetch = useCallback((linkElement) => {
    if (!linkElement) return;

    const handleMouseEnter = () => {
      if (linkElement.href) {
        prefetchForNavigation(linkElement.href);
      }
    };

    const handleFocus = () => {
      if (linkElement.href) {
        prefetchForNavigation(linkElement.href);
      }
    };

    linkElement.addEventListener('mouseenter', handleMouseEnter);
    linkElement.addEventListener('focus', handleFocus);

    return () => {
      linkElement.removeEventListener('mouseenter', handleMouseEnter);
      linkElement.removeEventListener('focus', handleFocus);
    };
  }, [prefetchForNavigation]);

  // Limpar prefetches obsoletos ao desmontar
  useEffect(() => {
    return () => {
      // Cancelar prefetches obsoletos
      prefetchIdRef.current = 0;
    };
  }, []);

  return {
    prefetchForCompany,
    prefetchForContext,
    prefetchForNavigation,
    getPrefetchedData,
    clearPrefetchCache,
    getPrefetchStats,
    setupHoverPrefetch,
    setupLinkPrefetch
  };
};

export default usePrefetch;
